// src/components/modules/dashboardApi.js
// Every backend call used by the Dashboard and Sidebar, in one place.
// To wire up a new API: set its path in ENDPOINTS, then check the response mapping
// in the matching fetch function (look for "RESPONSE SHAPE").

import { Timesheetapi } from "../../middleware/interceptor";
import { normalizeEntries, toISO } from "./dashboardStats";

export const ENDPOINTS = {
  // ── NEW — fill these in (leave "" until the API exists) ──
  myTimesheetEntries: "api/Dashboard/GetMyTimesheetEntries",       // DashboardController.cs
  pendingApprovalsCount: "api/OrgApprovals/PendingCount", // OrgApprovalsController.cs (approvals by reporting line)
  organizationSummary: "api/Dashboard/GetOrganizationSummary",     // OrganizationController.cs (admins)
  holidays: "api/Holiday/GetHolidays",                             // HolidayController.cs (GET ?year=YYYY)
  saveHoliday: "api/Holiday/SaveHoliday",                          // HolidayController.cs (Admin / HR)
  deleteHoliday: "api/Holiday/DeleteHoliday",                      // HolidayController.cs (Admin / HR)
  importHolidays: "api/Holiday/ImportHolidays",                    // HolidayController.cs (Admin / HR)

  // ── EXISTING — already in use ──
  clients: "api/Projects/getclientname",
  projects: "api/Projects/getprojectdetails",
  employees: "api/Projects/GetAllEmployee",
  permissions: "api/Roles/GetPermissionsByRoleId",
};

export class NotConfiguredError extends Error {
  constructor(name) {
    super(`Endpoint "${name}" is not configured in dashboardApi.js`);
    this.name = "NotConfiguredError";
  }
}

// ── 1. My timesheet entries (required for all personal sections) ─────
// year: which year to load (default this year) — Jan 1 → today, or → Dec 31 for a past year
export async function fetchMyEntries(empId, year = new Date().getFullYear()) {
  if (!ENDPOINTS.myTimesheetEntries) throw new NotConfiguredError("myTimesheetEntries");
  const now = new Date();
  const toDate = year >= now.getFullYear() ? toISO(now) : `${year}-12-31`;
  const res = await Timesheetapi.get(ENDPOINTS.myTimesheetEntries, {
    params: { empid: empId, fromDate: `${year}-01-01`, toDate },
  });
  // RESPONSE SHAPE: { status, entries: [{ entry_date, hours, is_billable, project_name, ... }], off_dates: [] }
  if (res?.data?.status === 0) throw new Error(res.data.message || "Timesheet entries API returned status 0");
  return {
    entries: normalizeEntries(res?.data?.entries ?? []),
    // Days covered by default rows (leave etc.) — not counted as missed
    offDates: Array.isArray(res?.data?.off_dates) ? res.data.off_dates : [],
    // HR joining / exit dates ("" if not on the HR list) — expected hours only count while employed
    joiningDate: res?.data?.date_of_joining || "",
    exitDate: res?.data?.date_of_exit || "",
    // "IN" | "US" (Users page → Location) — which holiday calendar applies to me
    country: res?.data?.country || "IN",
  };
}

// ── 2. Company holidays ──────────────────────────────────────────────
// Returns [{ id, date: "YYYY-MM-DD", dayName, name, type: "General" | "Restricted", updated }]
const mapHoliday = (h) => ({
  id: h.holiday_id,
  date: String(h.holiday_date ?? "").slice(0, 10),
  dayName: h.day_name ?? "",
  name: h.holiday_name ?? "",
  type: String(h.holiday_type ?? "General").toLowerCase().startsWith("r") ? "Restricted" : "General",
  updated: h.updated_date ?? "",
});

// Dashboard use: never throws — on failure the figures fall back to weekends only.
export async function fetchHolidays(year, country = "IN") {
  if (!ENDPOINTS.holidays) return [];
  try {
    return (await fetchHolidayCalendar(year, country)).holidays;
  } catch (err) {
    console.error("Holidays load failed (treating as none):", err);
    return [];
  }
}

// Holiday calendar page: { year, country, countries: [{ code, name, holidays }], years: [2026, ...], holidays: [...] }
// country: "IN" (India — the one the hours rules use; default) | "US". Throws on failure.
export async function fetchHolidayCalendar(year, country = "IN") {
  const res = await Timesheetapi.get(ENDPOINTS.holidays, { params: { year, country } });
  const d = res?.data;
  if (d?.status !== 1) throw new Error(d?.message || "Holidays load failed");
  return {
    year: d.year,
    country: d.country || country,
    countries: Array.isArray(d.countries) ? d.countries : [],
    years: Array.isArray(d.years) ? d.years : [],
    holidays: (d.holidays || []).map(mapHoliday).filter((h) => /^\d{4}-\d{2}-\d{2}$/.test(h.date)),
  };
}

const editor = () => ({
  empid: Number(localStorage.getItem("employee_id")) || 0,
  roleId: Number(localStorage.getItem("emp_role_id")) || 0,
});

// Add (no id) or edit one holiday. Returns the server message; throws with the server message on failure.
export async function saveHoliday({ id, date, name, type, country = "IN" }) {
  const res = await Timesheetapi.post(ENDPOINTS.saveHoliday, {
    ...editor(), country, holiday_id: id || 0, holiday_date: date, holiday_name: name, holiday_type: type,
  });
  if (res?.data?.status !== 1) throw new Error(res?.data?.message || "Couldn't save the holiday");
  return res.data.message;
}

export async function deleteHoliday(id) {
  const res = await Timesheetapi.post(ENDPOINTS.deleteHoliday, { ...editor(), holiday_id: id });
  if (res?.data?.status !== 1) throw new Error(res?.data?.message || "Couldn't remove the holiday");
  return res.data.message;
}

// rows: [{ date, name, type }]. dryRun = true only checks. Returns { added, skipped, message }.
export async function importHolidays(rows, { dryRun = false, country = "IN" } = {}) {
  const res = await Timesheetapi.post(ENDPOINTS.importHolidays, {
    ...editor(),
    country,
    dry_run: dryRun,
    holidays: rows.map((r) => ({ holiday_date: r.date, holiday_name: r.name, holiday_type: r.type })),
  });
  const d = res?.data;
  if (d?.status !== 1) {
    const err = new Error(d?.message || "Import failed");
    err.details = d?.errors || [];
    throw err;
  }
  return { added: d.added, skipped: d.skipped || [], message: d.message };
}

// ── 3. Pending approvals count (optional — Sidebar badge) ───────────
export async function fetchPendingApprovalsCount(empId) {
  if (!ENDPOINTS.pendingApprovalsCount) return 0;
  try {
    const res = await Timesheetapi.get(ENDPOINTS.pendingApprovalsCount, {
      params: { empid: empId, roleId: localStorage.getItem("emp_role_id") || "" },
    });
    // RESPONSE SHAPE: { count: 4 } or a plain number
    const n = Number(res?.data?.count ?? res?.data?.Count ?? res?.data ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch (err) {
    console.error("Pending approvals count failed:", err);
    return 0;
  }
}

// ── 3b. Approvals tab + badge, by Org Chart (OrgApprovalsController.cs) ──
// canApprove = someone reports to me on the Org Chart, or I'm the top of the chart. Role doesn't matter.
// count = this month's pending entries I may approve.
export async function fetchApprovalStatus(empId) {
  try {
    const res = await Timesheetapi.get(ENDPOINTS.pendingApprovalsCount, { params: { empid: empId } });
    const n = Number(res?.data?.count ?? 0);
    return { canApprove: res?.data?.status === 1 && res?.data?.canApprove === true, count: Number.isFinite(n) ? n : 0 };
  } catch (err) {
    console.error("Approval status failed:", err);
    return { canApprove: false, count: 0 };
  }
}

// ── 4. Sidebar permissions (existing) ───────────────────────────────
export async function fetchPermissions(roleId) {
  const res = await Timesheetapi.get(ENDPOINTS.permissions, { params: { roleId } });
  const map = {};
  (res?.data?.permissions || []).forEach((p) => {
    map[p.module_key] = !!p.has_access;
  });
  return map; // { dashboard: true, activity_card: true, ... }
}

// ── 5. Organization overview (existing APIs) ────────────────────────
export async function fetchOrgCounts(empId) {
  // allSettled: one failing API shouldn't blank the whole overview
  const [clientRes, projectRes, empRes] = await Promise.allSettled([
    Timesheetapi.get(ENDPOINTS.clients),
    Timesheetapi.get(ENDPOINTS.projects, { params: { Role_id: 3, empid: empId } }),
    Timesheetapi.post(ENDPOINTS.employees),
  ]);

  const out = {};

  if (clientRes.status === "fulfilled") {
    const clients = clientRes.value?.data?.Client || [];
    out.clients = new Set(clients.map((c) => c.Client_id ?? c.client_id)).size;
  } else console.error("Clients load failed:", clientRes.reason);

  if (projectRes.status === "fulfilled") {
    // De-dupe by project id first so active + inactive always adds up to total
    const byId = new Map();
    (projectRes.value?.data?.projects || []).forEach((p) => byId.set(p.Project_id ?? p.projectId, p));
    const projects = [...byId.values()];
    const isType = (p, t) => String(p.Project_type) === String(t);
    out.projects = projects.length;
    out.activeProjects = projects.filter((p) => String(p.status ?? p.Status) === "1").length;
    out.inactiveProjects = out.projects - out.activeProjects;
    out.opsProjects = projects.filter((p) => isType(p, 1)).length;
    out.onetimeProjects = projects.filter((p) => isType(p, 2)).length;
    out.annualProjects = projects.filter((p) => isType(p, 3)).length;
  } else console.error("Projects load failed:", projectRes.reason);

  if (empRes.status === "fulfilled") {
    const employees = empRes.value?.data?.Empdetails || [];
    out.activeEmployees = employees.filter((e) => {
      if (String(e.del_employee_status ?? "") === "1") return false;
      return e.employee_status == null ? true : String(e.employee_status) === "1";
    }).length;
  } else console.error("Employees load failed:", empRes.reason);

  return out;
}

// ── 6. Organisation view (admins): same figures as My view, summed across all employees ──
// Returned in the same shape the dashboard uses for personal stats, so one UI renders both.
// group: "all" | "freshworks" (people who log time in Rocketlane) | "legacy" (everyone else)
// month: "YYYY-MM" (empty = this month). MTD = that month, YTD = Jan → end of that month.
export async function fetchOrganizationSummary(group = "all", month = "") {
  const res = await Timesheetapi.get(ENDPOINTS.organizationSummary, {
    params: {
      empid: localStorage.getItem("employee_id") || "",
      roleId: localStorage.getItem("emp_role_id") || "",
      group,
      ...(month ? { month } : {}),
    },
  });
  const d = res?.data;
  if (d?.status !== 1) throw new Error(d?.message || "Organisation summary failed");

  const n = (x) => Number(x) || 0;
  const label = (ym) => new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1).toLocaleDateString("en-GB", { month: "short" });
  const logged = n(d.mtd.logged_hours);

  const day = (iso) => new Date(`${iso}T00:00:00`);
  return {
    group: d.group || group,
    employeeCount: n(d.employee_count),
    employeeSource: d.employee_source || "timesheet_accounts",
    groupCounts: d.group_counts || null,
    notInTimesheet: d.not_in_timesheet || [],   // on the HR list today, no login, no Rocketlane time
    notTracked: d.not_tracked || [],           // non-project staff (Timesheet_Required = 0)
    hoursOnly: !!d.hours_only,                 // "Not on HR list" group: no expected hours / missed entries
    rocketlaneUnmatched: d.rocketlane_unmatched || [],
    // Day-level detail behind Extra / Holiday hours (this year, all counted employees)
    extraDays: (d.extra_days || []).map((x) => ({
      iso: x.date, date: day(x.date), employeeId: x.employee_id, employeeName: x.employee_name,
      logged: n(x.logged_hours), regular: n(x.regular_hours), extra: n(x.extra_hours), reason: x.reason,
    })),
    holidayDays: (d.holiday_days || []).map((x) => ({
      iso: x.date, date: day(x.date), employeeId: x.employee_id, employeeName: x.employee_name,
      name: x.holiday_name, type: x.holiday_type, hours: n(x.hours),
    })),
    mtd: {
      billable: n(d.mtd.billable_hours),
      nonBillable: n(d.mtd.non_billable_hours),
      logged,
      expected: n(d.mtd.expected_hours),
      regular: n(d.mtd.regular_hours),
      extra: n(d.mtd.extra_hours),
      holiday: n(d.mtd.holiday_hours),
      notLogged: n(d.mtd.not_logged_hours),
      workingDays: n(d.mtd.working_days),
      billablePct: n(d.mtd.billable_pct),
      utilization: n(d.mtd.utilization_pct),
    },
    ytd: {
      billable: n(d.ytd.billable_hours),
      nonBillable: n(d.ytd.non_billable_hours),
      logged: n(d.ytd.logged_hours),
      expected: n(d.ytd.expected_hours),
      regular: n(d.ytd.regular_hours),
      extra: n(d.ytd.extra_hours),
      holiday: n(d.ytd.holiday_hours),
      billablePct: n(d.ytd.billable_pct),
      utilization: n(d.ytd.utilization_pct),
      missedDays: n(d.ytd.missed_entries),
    },
    missedCount: n(d.mtd.missed_entries),
    // How mtd.expected is built (person-days, all employees) — see OrganizationController

    missedDays: [], // no per-day list in Organisation view
    holidays: (d.holidays || []).map(mapHoliday).filter((h) => h.date.slice(0, 7) === String(d.as_of || "").slice(0, 7)),
    months: (d.months || []).map((m) => ({ label: label(m.month), billable: n(m.billable_hours), nonBillable: n(m.non_billable_hours) })),
    // "Client / Project" — many clients share project names such as "Operational Updates"
    projects: (d.projects || []).map((p) => ({
      name: p.label || (p.client_name ? `${p.client_name} / ${p.project_name}` : p.project_name),
      hours: n(p.hours),
      billableHours: n(p.billable_hours),
    })),
    employees: d.employees || [],
  };
}